import React, { forwardRef } from 'react';
import { useFormikContext } from 'formik';
import { Box } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { DeliveryListFormValues } from '../../types';
import {
    BasketIcon,
    CalculatorIcon,
    InventoryCompletedIcon,
    MoneyIcon,
    Typography,
} from '../../../../../../ui-kit';
import { usePriceService } from '../../../../../../utils/hooks/priceService';
import { UnitType } from '../../../../../../generated/graphql';
import { useUser } from '../../../../../../utils/hooks/auth';
import { CheckDeliveryItemWrapper, CheckDeliveryItemsWrapper, CheckDeliveryListWrapper, CircleIcon, Divider } from './style';





const CheckDeliveryList = forwardRef<HTMLDivElement>((_, ref) => {
    const { t } = useTranslation();
    const { user: { defaultCurrency: { currencyCode, precision: defaultPrecision } } } = useUser();
    const { values: { deliveryListItems, pendingListItems } } = useFormikContext<DeliveryListFormValues>();
    const { priceService } = usePriceService();
    const getLocalizedPrice = (price: number) => {
        const intPrice = priceService.toInteger(price, currencyCode);
        return priceService.toLocaleString(intPrice, currencyCode);
    };

    const getQuantityText = (quantity: number, unitType: UnitType) => `${quantity} ${unitType}`;

    const mappedPendingListItems: typeof deliveryListItems = pendingListItems.map(item => {
        const { quantity, description, finalAmount, unitOfMeasure, unitPriceWithoutVat, stockCard } = item;

        const totalCost = priceService.multiply(unitPriceWithoutVat || 0, quantity);

        return {
            quantity: finalAmount || 0,
            name: stockCard?.name || description,
            stockCardId: stockCard?.id || '',
            storageUnit: unitOfMeasure || UnitType.Pcs,
            priceWithoutVat: priceService.integerToFloatWithPrecision(unitPriceWithoutVat || 0, defaultPrecision),
            totalCost: priceService.integerToFloatWithPrecision(totalCost, defaultPrecision),
        };
    });
    const items = [...deliveryListItems, ...mappedPendingListItems];

    const itemsCount = items.length;
    const totalCost = items.reduce((acc, { totalCost: total }) => {
        const { amount, precision } = priceService.parseIntegerFromFloat(total);
        const normalizedAmount = priceService.getAmountWithPrecision(amount, precision, currencyCode);
        return priceService.add(normalizedAmount, acc);
    }, 0);

    const localizedTotalPrice = priceService.toLocaleString(totalCost, currencyCode);

    return (
        <CheckDeliveryListWrapper ref={ref}>
            <Box display='flex' alignItems='center'>
                <CircleIcon mr={0.5} fontSize='20' width={24} height={24}>
                    <BasketIcon fontSize='inherit' />
                </CircleIcon>
                <Typography>{t('{{count}} item in the list', { count: itemsCount })}</Typography>
            </Box>
            <Box display='flex' alignItems='center'>
                <CircleIcon mr={0.5} fontSize='20' width={24} height={24}>
                    <CalculatorIcon fontSize='inherit' />
                </CircleIcon>
                <Typography>{t('{{localizedTotalPrice}} is total cost', { localizedTotalPrice })}</Typography>
            </Box>
            {items.length > 0 ? <Divider /> : null}
            <CheckDeliveryItemsWrapper>
                {items.map(stockCard => (
                    <CheckDeliveryItemWrapper key={stockCard.stockCardId}>
                        <Typography variant='subtitle1' noWrap>
                            {stockCard.name}
                        </Typography>
                        <Box display='flex' justifyContent='flex-end'>
                            <Box mr={1} display='flex' alignItems='center'>
                                <CircleIcon mr={0.5} color='green' fontSize='14' width={16} height={16}>
                                    <InventoryCompletedIcon fontSize='inherit' />
                                </CircleIcon>
                                <Typography variant='caption'>
                                    {getQuantityText(stockCard.quantity, stockCard.storageUnit)}
                                </Typography>
                            </Box>
                            <Box display='flex' alignItems='center'>
                                <CircleIcon mr={0.5} color='green' fontSize='14' width={16} height={16}>
                                    <MoneyIcon fontSize='inherit' />
                                </CircleIcon>
                                <Typography variant='caption'>
                                    {getLocalizedPrice(stockCard.totalCost)}
                                </Typography>
                            </Box>
                        </Box>
                    </CheckDeliveryItemWrapper>
                ))}
            </CheckDeliveryItemsWrapper>
        </CheckDeliveryListWrapper>
    );
});

export default CheckDeliveryList;
